import { useEffect, useState } from "react";
import { LogOut, KeyRound, UserRound, Save, CreditCard } from "lucide-react";
import PasswordInput from "../../../components/PasswordInput";
import ClientService from "../../../services/client/ClientService";
import { showError, showSuccess, notifyApiError } from "../../../components/alertas";
import AvatarSelector from "../../../components/AvatarSelector";
import logger from "../../../utils/logger";
import { getSubscriptionStatus } from "../../../utils/subscriptionStatus";
import { computePrice, nextCycleAddons, addonsChangePending } from "../../superadmin/pricing";
import "./cuenta.css";

/** Mi cuenta: quién soy, avatar (con más de un profesional), contraseña y cerrar sesión. */
function AccountTab({ clientData, userData, handleClientUpdate, refetchClientData }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [avatarStyle, setAvatarStyle] = useState('notionists');
  const [savedSeed, setSavedSeed] = useState(null);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  const isAdmin = userData?.userType === 'admin';
  const currentStaff = clientData?.staff?.find((s) => s.name === userData?.username) || (isAdmin ? clientData?.staff?.[0] : null);

  useEffect(() => {
    if (!clientData || !userData) return;
    const style = clientData.personalization?.avatarStyle || 'notionists';
    setAvatarStyle(style);
    let staff = clientData?.staff?.find((s) => s.name === userData?.username);
    if (!staff && userData?.userType === 'admin') staff = clientData?.staff?.[0];
    if (staff?.avatarSeed) {
      setSavedSeed(staff.avatarSeed);
      setSelectedAvatar({ seed: staff.avatarSeed, style });
    }
  }, [clientData, userData]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmNewPassword) { showError("Campos requeridos", "Completa todos los campos."); return; }
    if (newPassword !== confirmNewPassword) { showError("Las contraseñas no coinciden", "Verifica la nueva contraseña."); return; }
    setIsChangingPassword(true);
    try {
      await ClientService.changePassword(clientData.id, { currentPassword, newPassword });
      showSuccess("Contraseña actualizada", "Tu contraseña fue cambiada correctamente.");
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (error) {
      showError("Error", error?.response?.data?.message || "No se pudo cambiar la contraseña.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = () => {
    ['clientId', 'turnlinkToken', 'customColors', 'brandSettings', 'userData'].forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
    document.cookie.split(";").forEach((c) => { document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`); });
    showSuccess("Sesión cerrada", "Has cerrado sesión correctamente.");
    setTimeout(() => window.location.reload(), 500);
  };

  const handleSaveAvatar = async () => {
    if (!selectedAvatar) { showError("Avatar no seleccionado", "Elige un avatar antes de guardar."); return; }
    setIsSavingAvatar(true);
    try {
      if (!currentStaff?.id) { showError("Error", "No se pudo identificar tu información de profesional."); return; }
      if (isAdmin) await handleClientUpdate({ personalization: { ...clientData.personalization, avatarStyle } }, 'personalization', null);
      await ClientService.updateStaffData(clientData.id, currentStaff.id, { avatarSeed: selectedAvatar.seed });
      await refetchClientData();
      showSuccess("Avatar actualizado", "Tu avatar ha sido guardado correctamente.");
      setSelectedAvatar(null);
    } catch (error) {
      logger.error("Error:", error);
      notifyApiError(error, "Guardar avatar");
    } finally {
      setIsSavingAvatar(false);
    }
  };

  const multiStaff = (clientData?.staff?.length || 0) > 1;

  // Suscripción (solo el admin la ve): plan, vigencia y asientos.
  const sub = clientData?.subscription;
  const subStatus = sub ? getSubscriptionStatus(sub.expiresAt, sub.isActive) : null;
  const seats = Number(sub?.seats) || 1;
  const seatsUsed = clientData?.staff?.length || 0;
  const pillClass = { active: "cp-pill-ok", grace: "cp-pill-warn", expired: "cp-pill-danger" }[subStatus?.key] || "";
  const fmtMoney = (n) => `$${Number(n).toLocaleString("es-MX")} MXN`;

  return (
    <section className="cp-account">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title">Mi cuenta</h2>
        </div>
      </header>

      <div className="cp-account-grid">
        <div className="cp-account-col">
          <article className="cp-card cp-account-who">
            <span className="cp-avatar cp-account-avatar"><UserRound size={22} /></span>
            <span className="cp-row-main">
              <span className="cp-row-title">{userData?.username || "—"}</span>
              <span className="cp-row-sub">{isAdmin ? "Administrador" : "Profesional"}{clientData?.business?.name ? ` · ${clientData.business.name}` : ""}</span>
            </span>
          </article>

          {isAdmin && sub && (
            <article className="cp-card cp-account-card" data-tour="cuenta.plan">
              <div className="cp-card-head">
                <h3 className="cp-card-title"><CreditCard size={18} /> Tu plan</h3>
                <span className={`cp-pill ${pillClass}`}>{subStatus.label}</span>
              </div>
              <dl className="cp-kv">
                <dt>Plan</dt>
                <dd>{sub.planName || "Base"} · {fmtMoney(computePrice(seats, sub.addons))}/mes</dd>
                <dt>Vigencia</dt>
                <dd>
                  {subStatus.expiresLabel ? `Hasta el ${subStatus.expiresLabel}` : "Sin fecha de corte"}
                  {subStatus.relative && <span className="cp-account-rel"> · {subStatus.relative}</span>}
                </dd>
                <dt>Asientos</dt>
                <dd>{seats} {seats === 1 ? "profesional" : "profesionales"} <span className="cp-account-rel">· {seatsUsed} en uso</span></dd>
                <dt>Lealtad</dt>
                <dd>
                  {sub.addons?.loyaltyCards ? "Tarjetas de lealtad incluidas" : <span className="cp-account-rel">No incluido · escríbenos para activarlo</span>}
                  {/* Un add-on entra o sale en el corte del ciclo, no a media mensualidad: se avisa qué cambia. */}
                  {!!sub.addons?.loyaltyCards !== !!nextCycleAddons(sub).loyaltyCards && (
                    <span className="cp-account-rel"> · desde tu próximo ciclo: {nextCycleAddons(sub).loyaltyCards ? "incluidas" : "no incluidas"}</span>
                  )}
                </dd>
                <dt>Asistente IA</dt>
                <dd>
                  {sub.addons?.aiAssistant ? "Asistente con IA incluido" : <span className="cp-account-rel">No incluido · escríbenos para activarlo</span>}
                  {!!sub.addons?.aiAssistant !== !!nextCycleAddons(sub).aiAssistant && (
                    <span className="cp-account-rel"> · desde tu próximo ciclo: {nextCycleAddons(sub).aiAssistant ? "incluido" : "no incluido"}</span>
                  )}
                  {addonsChangePending(sub) && (
                    <span className="cp-account-rel"> · próximo ciclo: {fmtMoney(computePrice(seats, nextCycleAddons(sub)))}/mes</span>
                  )}
                </dd>
              </dl>
              <p className="cp-hint">
                {subStatus.key === "grace"
                  ? "Estás en periodo de gracia: renueva para no perder el acceso."
                  : subStatus.key === "expired" || subStatus.key === "inactive"
                    ? "Tu suscripción no está activa. Escríbenos para renovarla."
                    : "Para cambiar de plan o agregar asientos, escríbenos por WhatsApp."}
              </p>
            </article>
          )}

          <article className="cp-card cp-account-card">
            <div className="cp-card-head"><h3 className="cp-card-title"><KeyRound size={18} /> Contraseña</h3></div>
            <form onSubmit={handleChangePassword} className="cp-form" data-tour="cuenta.password">
              <label className="cp-field"><span>Contraseña actual</span><PasswordInput className="cp-input" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={isChangingPassword} /></label>
              <div className="cp-grid-2">
                <label className="cp-field"><span>Nueva contraseña</span><PasswordInput className="cp-input" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={isChangingPassword} /></label>
                <label className="cp-field"><span>Confirmar</span><PasswordInput className="cp-input" autoComplete="new-password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} disabled={isChangingPassword} /></label>
              </div>
              <button type="submit" className="cp-btn cp-btn-primary cp-account-btn" disabled={isChangingPassword || !currentPassword || !newPassword || !confirmNewPassword}>
                {isChangingPassword ? <span className="cp-spin" /> : <KeyRound size={16} />} Cambiar contraseña
              </button>
            </form>
          </article>

          <article className="cp-card cp-account-card">
            <div className="cp-card-head"><h3 className="cp-card-title"><LogOut size={18} /> Sesión</h3></div>
            <p className="cp-muted">Al cerrar sesión se borran tus datos locales y tendrás que entrar de nuevo.</p>
            <button type="button" className="cp-btn cp-btn-danger cp-account-btn" onClick={handleLogout}><LogOut size={16} /> Cerrar sesión</button>
          </article>
        </div>

        {multiStaff && (
          <div className="cp-account-col">
            <article className="cp-card cp-account-card">
              <div className="cp-card-head"><h3 className="cp-card-title"><UserRound size={18} /> Avatar</h3></div>
              <p className="cp-muted">Con el que te ven tus clientes al elegir profesional.</p>
              <AvatarSelector selectedStyle={avatarStyle} onStyleChange={setAvatarStyle} onAvatarChange={setSelectedAvatar} showStyleSelector isAdmin={isAdmin} initialSeed={savedSeed} />
              <button type="button" className="cp-btn cp-btn-primary cp-account-btn" onClick={handleSaveAvatar} disabled={isSavingAvatar || !selectedAvatar}>
                {isSavingAvatar ? <span className="cp-spin" /> : <Save size={16} />} Guardar avatar
              </button>
            </article>
          </div>
        )}
      </div>
    </section>
  );
}

export default AccountTab;
